/*eslint-disable*/
import { debounce, merge } from 'lodash';

/*
 * A javascript-based implementation of Spatial Navigation.
 *
 * Copyright (c) 2017 Luke Chang.
 * https://github.com/luke-chang/js-spatial-navigation
 *
 * Licensed under the MPL 2.0.
 */
('use strict');

var SpatialNavigation;

/* ********************** */
/*  Global Configuration  */
/* ********************** */
// Note: an <extSelector> can be one of following types:
// - a valid selector string for "querySelectorAll" or jQuery (if it exists)
// - a NodeList or an array containing DOM elements
// - a single DOM element
// - a jQuery object
// - a string "@<sectionId>" to indicate the specified section
// - a string "@" to indicate the default section
var GlobalConfig = {
  selector: '', // can be a valid <extSelector> except "@" syntax.
  straightOnly: false,
  straightOverlapThreshold: 0.5,
  rememberSource: false,
  disabled: false,
  defaultElement: '', // <extSelector> except "@" syntax.
  enterTo: '', // '', 'last-focused', 'default-element'
  leaveFor: null, // {left: <extSelector>, right: <extSelector>,
  circular: null, // ['left', 'right', 'up', 'down']
  //  up: <extSelector>, down: <extSelector>}
  restrict: 'self-first', // 'self-first', 'self-only', 'none'
  tabIndexIgnoreList: 'a, input, select, textarea, button, iframe, [contentEditable=true]',
  navigableFilter: null,
  preventMouseNavigateIn: false,
  preventMouseNavigateWithin: false,
  debounce: {},
};

/* ******************* */
/*  Constant Variable  */
/* ******************* */
var KEYMAPPING = {
  37: 'left',
  38: 'up',
  39: 'right',
  40: 'down',
};

var ENTERKEY = 13;

var REVERSE = {
  left: 'right',
  up: 'down',
  right: 'left',
  down: 'up',
};

var parallelDirections = {
  up: 'left',
  left: 'top',
  down: 'left',
  right: 'top',
};

var opositeDirections = {
  up: 'bottom',
  left: 'right',
  down: 'top',
  right: 'left',
};

var signalComparations = {
  top: Math.min,
  left: Math.min,
  bottom: Math.max,
  right: Math.max,
};

var PARALLEL_TOLERANCE = 2;
var EVENT_PREFIX = 'sn:';
var ID_POOL_PREFIX = 'section-';

/* ****************** */
/*  Private Variable  */
/* ****************** */
var _idPool = 0;
var _ready = false;
var _pause = false;
var _sections = {};
var _debouncedKeyDownFn = {
  0: onDebounceKeyDown,
};
var _sectionCount = 0;
var _defaultSectionId = '';
var _lastSectionId = '';
var _duringFocusChange = false;
var _mouseEnabled = false;

/* ********** */
/*  Polyfill  */
/* ********** */
var elementMatchesSelector =
  Element.prototype.matches ||
  Element.prototype.matchesSelector ||
  Element.prototype.mozMatchesSelector ||
  Element.prototype.webkitMatchesSelector ||
  Element.prototype.msMatchesSelector ||
  Element.prototype.oMatchesSelector ||
  function(selector) {
    var matchedNodes = (this.parentNode || this.document).querySelectorAll(selector);
    return [].slice.call(matchedNodes).indexOf(this) >= 0;
  };

/* *************** */
/*  Core Function  */
/* *************** */
function getRect(elem) {
  var cr = elem.getBoundingClientRect();
  var rect = {
    left: cr.left,
    top: cr.top,
    right: cr.right,
    bottom: cr.bottom,
    width: cr.width,
    height: cr.height,
  };
  rect.element = elem;
  rect.center = {
    x: rect.left + Math.floor(rect.width / 2),
    y: rect.top + Math.floor(rect.height / 2),
  };
  rect.center.left = rect.center.right = rect.center.x;
  rect.center.top = rect.center.bottom = rect.center.y;
  return rect;
}

function partition(rects, targetRect, straightOverlapThreshold) {
  var groups = [[], [], [], [], [], [], [], [], []];
  var i, rect, center, x, y, groupId, threshold;

  for (i = 0; i < rects.length; i++) {
    rect = rects[i];
    center = rect.center;

    if (center.x < targetRect.left) {
      x = 0;
    } else if (center.x <= targetRect.right) {
      x = 1;
    } else {
      x = 2;
    }

    if (center.y < targetRect.top) {
      y = 0;
    } else if (center.y <= targetRect.bottom) {
      y = 1;
    } else {
      y = 2;
    }

    groupId = y * 3 + x;
    groups[groupId].push(rect);

    if ([0, 2, 6, 8].indexOf(groupId) !== -1) {
      threshold = straightOverlapThreshold;

      if (rect.left <= targetRect.right - targetRect.width * threshold) {
        if (groupId === 2) {
          groups[1].push(rect);
        } else if (groupId === 8) {
          groups[7].push(rect);
        }
      }

      if (rect.right >= targetRect.left + targetRect.width * threshold) {
        if (groupId === 0) {
          groups[1].push(rect);
        } else if (groupId === 6) {
          groups[7].push(rect);
        }
      }

      if (rect.top <= targetRect.bottom - targetRect.height * threshold) {
        if (groupId === 6) {
          groups[3].push(rect);
        } else if (groupId === 8) {
          groups[5].push(rect);
        }
      }

      if (rect.bottom >= targetRect.top + targetRect.height * threshold) {
        if (groupId === 0) {
          groups[3].push(rect);
        } else if (groupId === 2) {
          groups[5].push(rect);
        }
      }
    }
  }

  return groups;
}

function generateDistanceFunction(targetRect) {
  return {
    nearPlumbLineIsBetter: function(rect) {
      var d;
      if (rect.center.x < targetRect.center.x) {
        d = targetRect.center.x - rect.right;
      } else {
        d = rect.left - targetRect.center.x;
      }
      return d < 0 ? 0 : d;
    },
    nearHorizonIsBetter: function(rect) {
      var d;
      if (rect.center.y < targetRect.center.y) {
        d = targetRect.center.y - rect.bottom;
      } else {
        d = rect.top - targetRect.center.y;
      }
      return d < 0 ? 0 : d;
    },
    nearTargetLeftIsBetter: function(rect) {
      var d;
      if (rect.center.x < targetRect.center.x) {
        d = targetRect.left - rect.right;
      } else {
        d = rect.left - targetRect.left;
      }
      return d < 0 ? 0 : d;
    },
    nearTargetTopIsBetter: function(rect) {
      var d;
      if (rect.center.y < targetRect.center.y) {
        d = targetRect.top - rect.bottom;
      } else {
        d = rect.top - targetRect.top;
      }
      return d < 0 ? 0 : d;
    },
    topIsBetter: function(rect) {
      return rect.top;
    },
    bottomIsBetter: function(rect) {
      return -1 * rect.bottom;
    },
    leftIsBetter: function(rect) {
      return rect.left;
    },
    rightIsBetter: function(rect) {
      return -1 * rect.right;
    },
  };
}

function prioritize(priorities) {
  var destPriority = null;
  var i, destDistance, distance, delta;

  for (i = 0; i < priorities.length; i++) {
    if (priorities[i].group.length) {
      destPriority = priorities[i];
      break;
    }
  }

  if (!destPriority) {
    return null;
  }

  destDistance = destPriority.distance;

  destPriority.group.sort(function(a, b) {
    for (i = 0; i < destDistance.length; i++) {
      distance = destDistance[i];
      delta = distance(a) - distance(b);
      if (delta) {
        return delta;
      }
    }
    return 0;
  });

  return destPriority.group;
}

function navigate(target, direction, candidates, config) {
  var rects = [];
  var i, j, rect, targetRect, distanceFunction, groups, internalGroups, priorities, destGroup, dest;
  if (!target || !direction || !candidates || !candidates.length) {
    return null;
  }

  for (i = 0; i < candidates.length; i++) {
    rect = getRect(candidates[i]);
    if (rect) {
      rects.push(rect);
    }
  }
  if (!rects.length) {
    return null;
  }

  targetRect = getRect(target);
  if (!targetRect) {
    return null;
  }

  distanceFunction = generateDistanceFunction(targetRect);

  groups = partition(rects, targetRect, config.straightOverlapThreshold);

  internalGroups = partition(groups[4], targetRect.center, config.straightOverlapThreshold);

  switch (direction) {
    case 'left':
      priorities = [
        {
          group: internalGroups[0].concat(internalGroups[3]).concat(internalGroups[6]),
          distance: [distanceFunction.nearPlumbLineIsBetter, distanceFunction.topIsBetter],
        },
        {
          group: groups[3],
          distance: [distanceFunction.nearPlumbLineIsBetter, distanceFunction.topIsBetter],
        },
        {
          group: groups[0].concat(groups[6]),
          distance: [
            distanceFunction.nearHorizonIsBetter,
            distanceFunction.rightIsBetter,
            distanceFunction.nearTargetTopIsBetter,
          ],
        },
      ];
      break;
    case 'right':
      priorities = [
        {
          group: internalGroups[2].concat(internalGroups[5]).concat(internalGroups[8]),
          distance: [distanceFunction.nearPlumbLineIsBetter, distanceFunction.topIsBetter],
        },
        {
          group: groups[5],
          distance: [distanceFunction.nearPlumbLineIsBetter, distanceFunction.topIsBetter],
        },
        {
          group: groups[2].concat(groups[8]),
          distance: [
            distanceFunction.nearHorizonIsBetter,
            distanceFunction.leftIsBetter,
            distanceFunction.nearTargetTopIsBetter,
          ],
        },
      ];
      break;
    case 'up':
      priorities = [
        {
          group: internalGroups[0].concat(internalGroups[1]).concat(internalGroups[2]),
          distance: [distanceFunction.nearHorizonIsBetter, distanceFunction.leftIsBetter],
        },
        {
          group: groups[1],
          distance: [distanceFunction.nearHorizonIsBetter, distanceFunction.leftIsBetter],
        },
        {
          group: groups[0].concat(groups[2]),
          distance: [
            distanceFunction.nearPlumbLineIsBetter,
            distanceFunction.bottomIsBetter,
            distanceFunction.nearTargetLeftIsBetter,
          ],
        },
      ];
      break;
    case 'down':
      priorities = [
        {
          group: internalGroups[6].concat(internalGroups[7]).concat(internalGroups[8]),
          distance: [distanceFunction.nearHorizonIsBetter, distanceFunction.leftIsBetter],
        },
        {
          group: groups[7],
          distance: [distanceFunction.nearHorizonIsBetter, distanceFunction.leftIsBetter],
        },
        {
          group: groups[6].concat(groups[8]),
          distance: [
            distanceFunction.nearPlumbLineIsBetter,
            distanceFunction.topIsBetter,
            distanceFunction.nearTargetLeftIsBetter,
          ],
        },
      ];
      break;
    default:
      return null;
  }

  if (config.straightOnly) {
    priorities.pop();
  }

  destGroup = prioritize(priorities);
  if (!destGroup) {
    return null;
  }

  dest = null;
  if (
    config.rememberSource &&
    config.previous &&
    config.previous.destination === target &&
    config.previous.reverse === direction
  ) {
    for (j = 0; j < destGroup.length; j++) {
      if (destGroup[j].element === config.previous.target) {
        dest = destGroup[j].element;
        break;
      }
    }
  }

  if (!dest) {
    dest = destGroup[0].element;
  }

  return dest;
}

/* ****************** */
/*  Private Function  */
/* ****************** */
function generateId() {
  var id;
  while (true) {
    _idPool += 1;
    id = ID_POOL_PREFIX + String(_idPool);
    if (!_sections[id]) {
      break;
    }
  }
  return id;
}

function parseSelector(selector) {
  var result;
  if (typeof selector === 'string') {
    result = [].slice.call(document.querySelectorAll(selector));
  } else if (typeof selector === 'object' && selector.length) {
    result = [].slice.call(selector);
  } else if (typeof selector === 'object' && selector.nodeType === 1) {
    result = [selector];
  } else {
    result = [];
  }
  return result;
}

function matchSelector(elem, selector) {
  if (!document.activeElement || document.activeElement.tagName !== 'A') {
    return false;
  }

  if (typeof selector === 'string') {
    return elementMatchesSelector.call(elem, selector);
  } else if (typeof selector === 'object' && selector.length) {
    return selector.indexOf(elem) >= 0;
  } else if (typeof selector === 'object' && selector.nodeType === 1) {
    return elem === selector;
  }
  return false;
}

function getCurrentFocusedElement() {
  var activeElement = document.activeElement;
  if (activeElement && document.activeElement.dataset.navItem) {
    return activeElement;
  }
  if (_lastSectionId && _sections[_lastSectionId]) {
    return _sections[_lastSectionId].lastFocusedElement;
  }
  return document.querySelector('[tabindex="-1"]');
}

function extend(out) {
  var i, key;
  out = out || {};
  for (i = 1; i < arguments.length; i++) {
    if (!arguments[i]) {
      continue;
    }
    for (key in arguments[i]) {
      if (arguments[i].hasOwnProperty(key) && arguments[i][key] !== undefined) {
        out[key] = arguments[i][key];
      }
    }
  }
  return out;
}

function exclude(elemList, excludedElem) {
  var i, index;
  if (!Array.isArray(excludedElem)) {
    excludedElem = [excludedElem];
  }
  for (i = 0; i < excludedElem.length; i++) {
    index = elemList.indexOf(excludedElem[i]);
    if (index >= 0) {
      elemList.splice(index, 1);
    }
  }
  return elemList;
}

function isNavigable(elem, sectionId, verifySectionSelector) {
  if (!elem || !sectionId || !_sections[sectionId] || _sections[sectionId].disabled) {
    return false;
  }
  if ((elem.offsetWidth <= 0 && elem.offsetHeight <= 0) || elem.hasAttribute('disabled')) {
    return false;
  }
  if (verifySectionSelector && !matchSelector(elem, _sections[sectionId].selector)) {
    return false;
  }
  if (typeof _sections[sectionId].navigableFilter === 'function') {
    if (_sections[sectionId].navigableFilter(elem, sectionId) === false) {
      return false;
    }
  } else if (typeof GlobalConfig.navigableFilter === 'function') {
    if (GlobalConfig.navigableFilter(elem, sectionId) === false) {
      return false;
    }
  }
  return true;
}

function getSectionId(elem) {
  var id;
  for (id in _sections) {
    if (!_sections[id].disabled && matchSelector(elem, _sections[id].selector)) {
      return id;
    }
  }
  return null;
}

function getSectionNavigableElements(sectionId) {
  return parseSelector(_sections[sectionId].selector).filter(function(elem) {
    return isNavigable(elem, sectionId);
  });
}

function getSectionDefaultElement(sectionId) {
  var defaultElement = _sections[sectionId].defaultElement;
  if (!defaultElement) {
    return null;
  }
  if (typeof defaultElement === 'string') {
    defaultElement = parseSelector(defaultElement)[0];
  }
  if (isNavigable(defaultElement, sectionId, true)) {
    return defaultElement;
  }
  return null;
}

function getSectionLastFocusedElement(sectionId) {
  var lastFocusedElement = _sections[sectionId].lastFocusedElement;
  if (!isNavigable(lastFocusedElement, sectionId, true)) {
    return null;
  }
  return lastFocusedElement;
}

function fireEvent(elem, type, details, cancelable) {
  var evt = document.createEvent('CustomEvent');
  if (arguments.length < 4) {
    cancelable = true;
  }
  evt.initCustomEvent(EVENT_PREFIX + type, true, cancelable, details);
  return elem.dispatchEvent(evt);
}

function focusElement(elem, sectionId, direction) {
  var currentFocusedElement, unfocusProperties, focusProperties;
  if (!elem) {
    return false;
  }

  currentFocusedElement = getCurrentFocusedElement();

  _duringFocusChange = true;

  if (currentFocusedElement) {
    unfocusProperties = {
      nextElement: elem,
      nextSectionId: sectionId,
      direction: direction,
      native: false,
    };
    if (!fireEvent(currentFocusedElement, 'willunfocus', unfocusProperties)) {
      _duringFocusChange = false;
      return false;
    }
    currentFocusedElement.blur();
    fireEvent(currentFocusedElement, 'unfocused', unfocusProperties, false);
  }

  focusProperties = {
    previousElement: currentFocusedElement,
    sectionId: sectionId,
    direction: direction,
    native: false,
  };
  if (!fireEvent(elem, 'willfocus', focusProperties)) {
    _duringFocusChange = false;
    return false;
  }
  elem.focus({ preventScroll: true });
  fireEvent(elem, 'focused', focusProperties, false);

  _duringFocusChange = false;

  focusChanged(elem, sectionId);
  return true;
}

function focusChanged(elem, sectionId) {
  if (!sectionId) {
    sectionId = getSectionId(elem);
  }
  if (sectionId) {
    _sections[sectionId].lastFocusedElement = elem;
    _lastSectionId = sectionId;
  }
}

function focusExtendedSelector(selector, direction, checkOnly) {
  var sectionId, next, nextSectionId;
  if (typeof selector === 'string' && selector.charAt(0) === '@') {
    if (selector.length === 1) {
      return focusSection(null, checkOnly);
    }
    sectionId = selector.substr(1);
    return focusSection(sectionId, checkOnly);
  }
  next = parseSelector(selector)[0];
  if (next) {
    nextSectionId = getSectionId(next);
    if (isNavigable(next, nextSectionId)) {
      if (checkOnly) {
        return true;
      }
      return focusElement(next, nextSectionId, direction);
    }
  }
  return false;
}

function focusSection(sectionId, checkOnly) {
  var i, id, next;
  var range = [];
  var addRange = function(rangeId) {
    if (
      rangeId &&
      range.indexOf(rangeId) < 0 &&
      _sections[rangeId] &&
      !_sections[rangeId].disabled
    ) {
      range.push(rangeId);
    }
  };

  if (sectionId) {
    addRange(sectionId);
  } else {
    addRange(_defaultSectionId);
    addRange(_lastSectionId);
    Object.keys(_sections).map(addRange);
  }

  for (i = 0; i < range.length; i++) {
    id = range[i];

    if (_sections[id].enterTo === 'last-focused') {
      next =
        getSectionLastFocusedElement(id) ||
        getSectionDefaultElement(id) ||
        getSectionNavigableElements(id)[0];
    } else {
      next =
        getSectionDefaultElement(id) ||
        getSectionLastFocusedElement(id) ||
        getSectionNavigableElements(id)[0];
    }
    if (next) {
      if (checkOnly) {
        return true;
      }
      return focusElement(next, id);
    }
  }

  return false;
}

function fireNavigatefailed(elem, direction) {
  fireEvent(
    elem,
    'navigatefailed',
    {
      direction: direction,
    },
    false,
  );
}

function gotoLeaveFor(sectionId, direction, checkOnly = false) {
  var next, nextSectionId;

  if (_sections[sectionId].leaveFor && _sections[sectionId].leaveFor[direction] !== undefined) {
    next = _sections[sectionId].leaveFor[direction];
    if (typeof next === 'string') {
      if (next === '') {
        return null;
      }
      return focusExtendedSelector(next, direction, checkOnly);
    }

    nextSectionId = getSectionId(next);

    var canNavigate = isNavigable(next, nextSectionId);

    if (canNavigate && !checkOnly) {
      return focusElement(next, nextSectionId, direction);
    }

    if (checkOnly) {
      return canNavigate;
    }
  }
  return false;
}

function checkCircular(sectionId, direction) {
  if (isCircular(sectionId, direction) && !_sections[sectionId].disabled) {
    return goCircular(direction);
  }
  return false;
}

function goCircular(direction) {
  var similarElements = getSimilarElements(direction);
  var newFocusedElement = similarElements.reduce(compareElementCandidates(direction));
  var nextSectionId = getSectionId(newFocusedElement);
  return focusElement(newFocusedElement, nextSectionId);
}

function isCircular(sectionId, direction) {
  if (!!_sections[sectionId].circular) {
    return _sections[sectionId].circular.indexOf(direction) !== -1;
  }
  return false;
}

function getSimilarElements(direction) {
  var currentFocusedElement = getSectionLastFocusedElement(_lastSectionId);
  var sectionNavigableElements = getSectionNavigableElements(_lastSectionId);
  return sectionNavigableElements.filter(
    compareSimilarityForElements(currentFocusedElement, direction),
  );
}

function compareSimilarityForElements(currentFocusedElement, direction) {
  var parallelDirection = getParallelDirection(direction);
  var currentParallelValue = currentFocusedElement.getBoundingClientRect()[parallelDirection];
  return function(navigableElement) {
    // We have a tolerance +PARALLEL_TOLERANCE/-PARALLEL_TOLERANCE pixels to calculate the similar elements.
    return (
      Math.abs(navigableElement.getBoundingClientRect()[parallelDirection] - currentParallelValue) <
      PARALLEL_TOLERANCE
    );
  };
}

function compareElementCandidates(direction) {
  var opositeDirection = getOpositeDirection(direction);
  var signalFn = getSignalComparation(opositeDirection);
  // This is a mini improve of performance.
  // store the prev value so we can be reused it for comparations
  var _aCompared = null;
  var _bCompared = null;
  return function(a, b) {
    _bCompared = b.getBoundingClientRect()[opositeDirection];
    if (!_aCompared) {
      _aCompared = a.getBoundingClientRect()[opositeDirection];
    }
    if (signalFn(_aCompared, _bCompared) === _aCompared) {
      return a;
    }
    _aCompared = _bCompared;
    return b;
  };
}

function getParallelDirection(direction) {
  return parallelDirections[direction];
}

function getOpositeDirection(direction) {
  return opositeDirections[direction];
}

function getSignalComparation(direction) {
  return signalComparations[direction];
}

function focusNext(direction, currentFocusedElement, currentSectionId, checkOnly = false) {
  var id, config, next, currentSectionNavigableElements, nextSectionId, result, enterToElement;
  var sectionNavigableElements = {};
  var allNavigableElements = [];
  var extSelector = currentFocusedElement.getAttribute('data-sn-' + direction);
  if (typeof extSelector === 'string') {
    if (extSelector === '' || !focusExtendedSelector(extSelector, direction)) {
      fireNavigatefailed(currentFocusedElement, direction);
      return false;
    }
    return true;
  }

  for (id in _sections) {
    if (_sections.hasOwnProperty(id)) {
      sectionNavigableElements[id] = getSectionNavigableElements(id);
      allNavigableElements = allNavigableElements.concat(sectionNavigableElements[id]);
    }
  }

  config = extend({}, GlobalConfig, _sections[currentSectionId]);

  if (config.restrict === 'self-only' || config.restrict === 'self-first') {
    currentSectionNavigableElements = sectionNavigableElements[currentSectionId];

    next = navigate(
      currentFocusedElement,
      direction,
      exclude(currentSectionNavigableElements, currentFocusedElement),
      config,
    );

    if (!next && config.restrict === 'self-first') {
      next = navigate(
        currentFocusedElement,
        direction,
        exclude(allNavigableElements, currentSectionNavigableElements),
        config,
      );
    }
  } else {
    next = navigate(
      currentFocusedElement,
      direction,
      exclude(allNavigableElements, currentFocusedElement),
      config,
    );
  }

  if (next) {
    _sections[currentSectionId].previous = {
      target: currentFocusedElement,
      destination: next,
      reverse: REVERSE[direction],
    };

    nextSectionId = getSectionId(next);

    if (currentSectionId !== nextSectionId) {
      if (checkOnly && _sections[nextSectionId].preventMouseNavigateIn) {
        return false;
      }
      result = gotoLeaveFor(currentSectionId, direction, checkOnly);
      if (result) {
        return true;
      } else if (result === null) {
        !checkOnly && fireNavigatefailed(currentFocusedElement, direction);
        return false;
      }

      switch (_sections[nextSectionId].enterTo) {
        case 'last-focused':
          enterToElement =
            getSectionLastFocusedElement(nextSectionId) || getSectionDefaultElement(nextSectionId);
          break;
        case 'default-element':
          enterToElement = getSectionDefaultElement(nextSectionId);
          break;
      }
      if (enterToElement) {
        next = enterToElement;
      }
    }

    if (checkOnly) {
      return !_sections[currentSectionId].preventMouseNavigateWithin;
    }

    return focusElement(next, nextSectionId, direction);
  } else if (gotoLeaveFor(currentSectionId, direction, checkOnly)) {
    return true;
  } else if (checkOnly) {
    return false;
  } else if (checkCircular(currentSectionId, direction)) {
    return true;
  }

  fireNavigatefailed(currentFocusedElement, direction);
  return false;
}

function onKeyDown(evt) {
  var currentFocusedElement, currentSectionId, sectionIdConfig;
  currentFocusedElement = getCurrentFocusedElement();
  currentSectionId = getSectionId(currentFocusedElement);
  sectionIdConfig = _sections[currentSectionId];
  if (sectionIdConfig) {
    return _debouncedKeyDownFn[sectionIdConfig.debounce.time](evt);
  }
  return false;
}

function onDebounceKeyDown(evt) {
  var currentFocusedElement, direction, currentSectionId, willmoveProperties;
  var preventDefault = function() {
    evt.preventDefault();
    evt.stopPropagation();
    return false;
  };
  SpatialNavigation.stopMouse();
  if (!_sectionCount || _pause || evt.altKey || evt.ctrlKey || evt.metaKey || evt.shiftKey) {
    return preventDefault();
  }

  direction = KEYMAPPING[evt.keyCode];
  if (!direction) {
    if (evt.keyCode === ENTERKEY) {
      currentFocusedElement = getCurrentFocusedElement();
      if (currentFocusedElement && getSectionId(currentFocusedElement)) {
        if (!fireEvent(currentFocusedElement, 'enter-down')) {
          return preventDefault();
        }
      }
    }
    return preventDefault();
  }

  currentFocusedElement = getCurrentFocusedElement();

  if (!currentFocusedElement) {
    if (_lastSectionId && _sections[_lastSectionId]) {
      currentFocusedElement = getSectionLastFocusedElement(_lastSectionId);
    }
    if (!currentFocusedElement) {
      focusSection();
      return preventDefault();
    }
  }

  currentSectionId = getSectionId(currentFocusedElement);
  if (!currentSectionId) {
    return preventDefault();
  }

  willmoveProperties = {
    direction: direction,
    sectionId: currentSectionId,
    cause: 'keydown',
  };

  if (fireEvent(currentFocusedElement, 'willmove', willmoveProperties)) {
    focusNext(direction, currentFocusedElement, currentSectionId);
  }

  return preventDefault();
}

function onFocus(evt) {
  var sectionId, focusProperties;
  var target = evt.target;
  if (target !== window && target !== document && _sectionCount && !_duringFocusChange) {
    sectionId = getSectionId(target);
    if (sectionId) {
      if (_pause) {
        focusChanged(target, sectionId);
        return;
      }

      focusProperties = {
        sectionId: sectionId,
        mouseEnabled: _mouseEnabled,
        native: true,
      };

      if (!fireEvent(target, 'willfocus', focusProperties)) {
        _duringFocusChange = true;
        target.blur();
        _duringFocusChange = false;
      } else {
        fireEvent(target, 'focused', focusProperties, false);
        focusChanged(target, sectionId);
      }
    }
  }
}

function onBlur(evt) {
  var unfocusProperties;
  var target = evt.target;
  if (
    target !== window &&
    target !== document &&
    !_pause &&
    _sectionCount &&
    !_duringFocusChange &&
    getSectionId(target)
  ) {
    unfocusProperties = {
      native: true,
    };
    if (!fireEvent(target, 'willunfocus', unfocusProperties)) {
      _duringFocusChange = true;
      setTimeout(function() {
        target.focus({ preventScroll: true });
        _duringFocusChange = false;
      });
    } else {
      fireEvent(target, 'unfocused', unfocusProperties, false);
    }
  }
}

function onMouseOver(evt) {
  var element;
  evt.preventDefault();
  if (_mouseEnabled) {
    element = getFirstTargeredElement(evt.target);
    if (element) element.focus({ preventScroll: true });
  }
}

function onMouseMove(evt) {
  evt.preventDefault();
  SpatialNavigation.resumeMouse();
}

function getFirstTargeredElement(element) {
  if (element.dataset && element.dataset.navItem === '') return element;
  return null;
}

function mapKeys(options) {
  var key;
  if (options) {
    if (options.keyMapping) {
      KEYMAPPING = {};
      for (key in options.keyMapping) {
        if (options.keyMapping.hasOwnProperty(key)) {
          KEYMAPPING[key] = options.keyMapping[key];
        }
      }
    }
    if (options.enterMapping) {
      ENTERKEY = options.enterMapping;
    }
  }
}

/* ***************** */
/*  Public Function  */
/* ***************** */
SpatialNavigation = {
  init: function(options) {
    if (!_ready) {
      mapKeys(options);
      document.body.addEventListener('keydown', onKeyDown);
      document.body.addEventListener('focus', onFocus, true);
      document.body.addEventListener('blur', onBlur, true);
      document.body.addEventListener('mouseover', onMouseOver, false);
      document.body.addEventListener('mousemove', onMouseMove, false);
      _ready = true;
    }
  },

  uninit: function() {
    document.body.removeEventListener('blur', onBlur, true);
    document.body.removeEventListener('focus', onFocus, true);
    document.body.removeEventListener('keydown', onKeyDown);
    document.body.removeEventListener('mouseover', onMouseOver);
    document.body.removeEventListener('mousemove', onMouseMove);
    SpatialNavigation.clear();
    _idPool = 0;
    _ready = false;
  },

  clear: function() {
    _sections = {};
    _sectionCount = 0;
    _defaultSectionId = '';
    _lastSectionId = '';
    _duringFocusChange = false;
  },

  // set(<config>);
  // set(<sectionId>, <config>);
  set: function() {
    var sectionId, config, key;

    if (typeof arguments[0] === 'object') {
      config = arguments[0];
    } else if (typeof arguments[0] === 'string' && typeof arguments[1] === 'object') {
      sectionId = arguments[0];
      config = arguments[1];
      if (!_sections[sectionId]) {
        throw new Error('Section "' + sectionId + '" doesn\'t exist!');
      }
    } else {
      return;
    }

    for (key in config) {
      if (GlobalConfig[key] !== undefined) {
        if (sectionId) {
          _sections[sectionId][key] = config[key];
        } else if (config[key] !== undefined) {
          GlobalConfig[key] = config[key];
        }
      }
    }

    if (sectionId) {
      // remove "undefined" items
      _sections[sectionId] = extend({}, _sections[sectionId]);
    }

    // Default values for debounce config
    _sections[sectionId].debounce = merge({ time: 0 }, _sections[sectionId].debounce);
    var debounceConfig = _sections[sectionId].debounce;
    if (!_debouncedKeyDownFn[debounceConfig.time]) {
      _debouncedKeyDownFn[debounceConfig.time] = debounce(
        onDebounceKeyDown,
        debounceConfig.time,
        debounceConfig.options,
      );
    }
  },

  // add(<config>);
  // add(<sectionId>, <config>);
  add: function() {
    var sectionId;
    var config = {};

    if (typeof arguments[0] === 'object') {
      config = arguments[0];
    } else if (typeof arguments[0] === 'string' && typeof arguments[1] === 'object') {
      sectionId = arguments[0];
      config = arguments[1];
    }

    if (!sectionId) {
      sectionId = typeof config.id === 'string' ? config.id : generateId();
    }

    if (_sections[sectionId]) {
      throw new Error('Section "' + sectionId + '" has already existed!');
    }

    _sections[sectionId] = {};
    _sectionCount += 1;

    SpatialNavigation.set(sectionId, config);

    return sectionId;
  },

  remove: function(sectionId) {
    if (!sectionId || typeof sectionId !== 'string') {
      throw new Error('Please assign the "sectionId"!');
    }
    if (_sections[sectionId]) {
      _sections[sectionId] = undefined;
      _sections = extend({}, _sections);
      _sectionCount -= 1;
      return true;
    }
    return false;
  },

  disable: function(sectionId) {
    if (_sections[sectionId]) {
      _sections[sectionId].disabled = true;
      return true;
    }
    return false;
  },

  enable: function(sectionId) {
    if (_sections[sectionId]) {
      _sections[sectionId].disabled = false;
      return true;
    }
    return false;
  },

  pause: function() {
    _pause = true;
  },

  resume: function() {
    _pause = false;
  },

  stopMouse: function() {
    _mouseEnabled = false;
  },

  resumeMouse: function() {
    _mouseEnabled = true;
  },

  // focus([silent])
  // focus(<sectionId>, [silent])
  // focus(<extSelector>, [silent])
  // Note: "silent" is optional and default to false
  focus: function(elem, silent) {
    var result = false;
    var autoPause;
    if (silent === undefined && typeof elem === 'boolean') {
      silent = elem;
      elem = undefined;
    }

    autoPause = !_pause && silent;

    if (autoPause) {
      SpatialNavigation.pause();
    }

    if (!elem) {
      result = focusSection();
    } else if (typeof elem === 'string') {
      if (_sections[elem]) {
        result = focusSection(elem);
      } else {
        result = focusExtendedSelector(elem);
      }
    } else {
      result = focusExtendedSelector(elem);
    }

    if (autoPause) {
      SpatialNavigation.resume();
    }

    return result;
  },

  // move(<direction>)
  // move(<direction>, <selector>)
  move: function(direction, selector) {
    var elem, sectionId, willmoveProperties;
    direction = direction.toLowerCase();
    if (!REVERSE[direction]) {
      return false;
    }

    elem = selector ? parseSelector(selector)[0] : getCurrentFocusedElement();
    if (!elem) {
      return false;
    }

    sectionId = getSectionId(elem);
    if (!sectionId) {
      return false;
    }

    willmoveProperties = {
      direction: direction,
      sectionId: sectionId,
      cause: 'api',
    };

    if (!fireEvent(elem, 'willmove', willmoveProperties)) {
      return false;
    }
    return focusNext(direction, elem, sectionId);
  },

  // makeFocusable()
  // makeFocusable(<sectionId>)
  makeFocusable: function(sectionId) {
    var id;
    var doMakeFocusable = function(section) {
      var tabIndexIgnoreList =
        section.tabIndexIgnoreList !== undefined
          ? section.tabIndexIgnoreList
          : GlobalConfig.tabIndexIgnoreList;
      parseSelector(section.selector).forEach(function(elem) {
        if (!matchSelector(elem, tabIndexIgnoreList)) {
          if (!elem.getAttribute('tabindex')) {
            elem.setAttribute('tabindex', '-1');
          }
        }
      });
    };

    if (sectionId) {
      if (_sections[sectionId]) {
        doMakeFocusable(_sections[sectionId]);
      } else {
        throw new Error('Section "' + sectionId + '" doesn\'t exist!');
      }
    } else {
      for (id in _sections) {
        if (_sections.hasOwnProperty(id)) {
          doMakeFocusable(_sections[id]);
        }
      }
    }
  },

  setDefaultSection: function(sectionId) {
    if (!sectionId) {
      _defaultSectionId = '';
    } else if (!_sections[sectionId]) {
      throw new Error('Section "' + sectionId + '" doesn\'t exist!');
    } else {
      _defaultSectionId = sectionId;
    }
  },

  getSectionId: getSectionId,
  generateId: generateId,
  focusNext: focusNext,
  gotoLeaveFor: gotoLeaveFor,
};

export default SpatialNavigation;

/*eslint-enable*/
